import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingCart, Trash2, Plus, Minus, CreditCard, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const schools = [
  { value: "University of Tulsa", label: "University of Tulsa" },
  { value: "Oral Roberts University", label: "Oral Roberts University" },
  { value: "Other", label: "Other" }
];

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartCount } = useCart();
  const [isCheckout, setIsCheckout] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  
  const [formData, setFormData] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    student_name: "",
    student_school: "",
    delivery_address: "",
    dorm_info: "",
    custom_message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitOrder = async () => {
    if (!formData.parent_name || !formData.parent_email || !formData.parent_phone ||
        !formData.student_name || !formData.student_school || !formData.delivery_address) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Create an order for each item in cart
      for (const item of cartItems) {
        const orderData = {
          ...formData,
          selected_package_id: item.id,
          selected_package_name: `${item.name} (x${item.quantity})`
        };
        await axios.post(`${API}/orders`, orderData);
      }
      
      // Send email notification via Formspree
      const cartSummary = cartItems.map(item => `${item.name} x${item.quantity}`).join(", ");
      const emailData = {
        subject: `New Order from ${formData.parent_name}`,
        parent_name: formData.parent_name,
        parent_email: formData.parent_email,
        parent_phone: formData.parent_phone,
        student_name: formData.student_name,
        student_school: formData.student_school,
        delivery_address: formData.delivery_address,
        dorm_info: formData.dorm_info || "N/A",
        custom_message: formData.custom_message || "No message",
        packages_ordered: cartSummary,
        total_items: cartItems.reduce((sum, item) => sum + item.quantity, 0),
        order_date: new Date().toLocaleString()
      };

      await fetch("https://formspree.io/f/mojndwzd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(emailData)
      });
      
      toast.success("Order placed successfully! We'll be in touch soon.");
      clearCart();
      setOrderComplete(true);
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen py-20" data-testid="order-complete">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-sm">
            <div className="w-20 h-20 mx-auto bg-brand-primary/10 rounded-full flex items-center justify-center mb-6">
              <ShoppingCart size={40} className="text-brand-primary" />
            </div>
            <h1 className="font-heading text-3xl text-brand-dark mb-4">Order Received!</h1>
            <p className="font-body text-brand-muted mb-8">
              Thank you for your order. We've received your care package request and will be in touch soon with next steps.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-8 py-4 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen py-20" data-testid="empty-cart">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-sm">
            <ShoppingCart size={64} className="mx-auto text-brand-muted/40 mb-6" />
            <h1 className="font-heading text-3xl text-brand-dark mb-4">Your Cart is Empty</h1>
            <p className="font-body text-brand-muted mb-8">
              Looks like you haven't added any care packages yet. Browse our selection and find the perfect package for your student!
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              Browse Packages
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-20" data-testid="cart-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => isCheckout ? setIsCheckout(false) : navigate("/shop")}
            className="p-2 rounded-full hover:bg-brand-secondary/20 transition-colors"
          >
            <ArrowLeft size={24} className="text-brand-dark" />
          </button>
          <h1 className="font-heading text-3xl text-brand-dark">
            {isCheckout ? "Checkout" : "Your Cart"}
          </h1>
          <span className="font-body text-brand-muted">({getCartCount()} items)</span>
        </div>

        {!isCheckout ? (
          /* Cart View */
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <Card key={item.id} className="bg-white shadow-sm" data-testid={`cart-item-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-heading text-lg text-brand-dark">{item.name}</h3>
                        <p className="font-body text-sm text-brand-muted mb-2">{item.description}</p>
                        <p className="font-body text-sm font-medium text-brand-coral">{item.price}</p>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1 rounded-full bg-brand-secondary/30 hover:bg-brand-secondary/50 transition-colors"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="font-body font-medium w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1 rounded-full bg-brand-secondary/30 hover:bg-brand-secondary/50 transition-colors"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div>
              <Card className="bg-white shadow-sm sticky top-32">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex justify-between font-body text-sm">
                        <span className="text-brand-muted">{item.name} x{item.quantity}</span>
                        <span className="text-brand-dark">{item.price}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-body">
                      <span className="font-medium text-brand-dark">Total Items</span>
                      <span className="font-medium text-brand-primary">{getCartCount()}</span>
                    </div>
                    <p className="font-body text-xs text-brand-muted mt-2">
                      * Actual pricing will be calculated at checkout when Stripe is integrated
                    </p>
                  </div>
                  <button
                    onClick={() => setIsCheckout(true)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                    data-testid="proceed-to-checkout"
                  >
                    <CreditCard size={18} />
                    Proceed to Checkout
                  </button>
                  <button
                    onClick={clearCart}
                    className="w-full px-6 py-3 rounded-full border-2 border-red-400 text-red-500 font-body font-medium hover:bg-red-50 transition-colors"
                    data-testid="clear-cart"
                  >
                    Clear Cart
                  </button>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* Checkout Form */
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Delivery Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Parent Info */}
                  <div>
                    <h3 className="font-heading text-lg text-brand-dark mb-4">Your Information</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="parent_name" className="font-body font-medium text-brand-dark">Your Name *</Label>
                        <Input
                          id="parent_name"
                          name="parent_name"
                          value={formData.parent_name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          className="mt-2"
                          data-testid="checkout-parent-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="parent_email" className="font-body font-medium text-brand-dark">Email *</Label>
                        <Input
                          id="parent_email"
                          name="parent_email"
                          type="email"
                          value={formData.parent_email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          className="mt-2"
                          data-testid="checkout-parent-email"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="parent_phone" className="font-body font-medium text-brand-dark">Phone *</Label>
                        <Input
                          id="parent_phone"
                          name="parent_phone"
                          type="tel"
                          value={formData.parent_phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                          className="mt-2"
                          data-testid="checkout-parent-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Student Info */}
                  <div className="border-t pt-6">
                    <h3 className="font-heading text-lg text-brand-dark mb-4">Student Information</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="student_name" className="font-body font-medium text-brand-dark">Student's Name *</Label>
                        <Input
                          id="student_name"
                          name="student_name"
                          value={formData.student_name}
                          onChange={handleChange}
                          placeholder="Enter student's name"
                          className="mt-2"
                          data-testid="checkout-student-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="student_school" className="font-body font-medium text-brand-dark">School *</Label>
                        <Select 
                          value={formData.student_school} 
                          onValueChange={(value) => handleSelectChange("student_school", value)}
                        >
                          <SelectTrigger className="mt-2" data-testid="checkout-student-school">
                            <SelectValue placeholder="Select school" />
                          </SelectTrigger>
                          <SelectContent>
                            {schools.map(school => (
                              <SelectItem key={school.value} value={school.value}>
                                {school.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="delivery_address" className="font-body font-medium text-brand-dark">Delivery Address *</Label>
                        <Textarea
                          id="delivery_address"
                          name="delivery_address"
                          value={formData.delivery_address}
                          onChange={handleChange}
                          placeholder="Enter full delivery address"
                          rows={3}
                          className="mt-2"
                          data-testid="checkout-delivery-address"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="dorm_info" className="font-body font-medium text-brand-dark">Dorm/Room Info (Optional)</Label>
                        <Input
                          id="dorm_info"
                          name="dorm_info"
                          value={formData.dorm_info}
                          onChange={handleChange}
                          placeholder="e.g., Smith Hall, Room 204"
                          className="mt-2"
                          data-testid="checkout-dorm-info"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Custom Message */}
                  <div className="border-t pt-6">
                    <Label htmlFor="custom_message" className="font-body font-medium text-brand-dark">
                      Personal Message (Optional)
                    </Label>
                    <Textarea
                      id="custom_message"
                      name="custom_message"
                      value={formData.custom_message}
                      onChange={handleChange}
                      placeholder="Write a heartfelt message to include with the package..."
                      rows={4}
                      className="mt-2"
                      data-testid="checkout-custom-message"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="bg-white shadow-sm sticky top-32">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex justify-between font-body text-sm">
                        <span className="text-brand-muted">{item.name} x{item.quantity}</span>
                        <span className="text-brand-dark">{item.price}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-body">
                      <span className="font-medium text-brand-dark">Total Items</span>
                      <span className="font-medium text-brand-primary">{getCartCount()}</span>
                    </div>
                  </div>
                  <div className="bg-brand-secondary/20 rounded-lg p-4 text-center">
                    <CreditCard className="mx-auto text-brand-primary mb-2" size={24} />
                    <p className="font-body text-sm text-brand-muted">
                      Payment processing via Stripe will be added soon
                    </p>
                  </div>
                  <button
                    onClick={handleSubmitOrder}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-coral text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    data-testid="place-order"
                  >
                    {isSubmitting ? "Placing Order..." : "Place Order"}
                  </button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
